import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Box, Check, Info, PlaneTakeoff, Plus } from 'lucide-react';
import { PRICING_US_HOME, buildUsHomeVolumePlans } from '../lib/pricing';
import { US_HOME_PRICING_SECTION } from '../lib/usPricingSectionCopy';

type Tab = 'standard' | 'drone';

const HomePricing: React.FC = () => {
  const [tab, setTab] = useState<Tab>('standard');

  const plans = useMemo(() => {
    const base = tab === 'standard' ? PRICING_US_HOME.extraCredit.withoutDrone : PRICING_US_HOME.extraCredit.withDrone;
    return buildUsHomeVolumePlans(base);
  }, [tab]);

  const FeatureRow = ({
    text,
    highlight = false,
    isExtra = false,
    useInfoIcon = false,
  }: {
    text: string;
    highlight?: boolean;
    isExtra?: boolean;
    useInfoIcon?: boolean;
  }) => {
    return (
      <div className="flex items-center justify-between gap-2 py-2 border-b border-gray-50 last:border-0 group">
        <div className="flex items-center min-w-0 flex-1">
          {useInfoIcon ? (
            <span className="mr-2 sm:mr-3 shrink-0">
              <Info className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
            </span>
          ) : (
            <div
              className={`p-1 rounded-full mr-2 sm:mr-3 shrink-0 ${
                isExtra ? 'bg-[#3C59B2]/10' : highlight ? 'bg-[#3C59B2]/10' : 'bg-green-50'
              }`}
            >
              {isExtra ? (
                <Plus className="w-3 h-3 text-[#3C59B2]" />
              ) : highlight ? (
                <PlaneTakeoff className="w-3 h-3 text-[#3C59B2]" />
              ) : (
                <Check className="w-3 h-3 text-green-500" />
              )}
            </div>
          )}
          <span
            className={`text-xs sm:text-sm whitespace-pre-line ${
              isExtra ? 'font-medium text-[#3C59B2]' : highlight ? 'font-medium text-[#3C59B2]' : 'text-gray-600'
            }`}
          >
            {text}
          </span>
        </div>
      </div>
    );
  };

  return (
    <section id="pricing" className="py-12 sm:py-16 md:py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-10 sm:mb-12 md:mb-16">
          <motion.h2
            className="font-manrope text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-3 sm:mb-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {US_HOME_PRICING_SECTION.title}
          </motion.h2>
          <motion.p
            className="text-base sm:text-lg md:text-xl text-gray-600"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            {US_HOME_PRICING_SECTION.subtitle}
          </motion.p>

          <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row gap-3 justify-center items-center">
            <button
              onClick={() => setTab('standard')}
              className={`btn-premium shrink-0 whitespace-nowrap px-6 py-3 rounded-full font-semibold text-sm transition-all duration-300 ${
                tab === 'standard'
                  ? 'bg-[#3C59B2] text-white'
                  : 'bg-white border-2 border-[#3C59B2] text-[#3C59B2] hover:bg-slate-50'
              }`}
            >
              {US_HOME_PRICING_SECTION.standardTab}
            </button>
            <button
              onClick={() => setTab('drone')}
              className={`btn-premium shrink-0 whitespace-nowrap px-6 py-3 rounded-full font-semibold text-sm transition-all duration-300 ${
                tab === 'drone'
                  ? 'bg-[#3C59B2] text-white'
                  : 'bg-white border-2 border-[#3C59B2] text-[#3C59B2] hover:bg-slate-50'
              }`}
            >
              {US_HOME_PRICING_SECTION.droneTab}
            </button>
          </div>

          {/* Info message */}
          <div className="mt-6 flex justify-center">
            <div className="inline-flex items-center gap-3 bg-white border border-[#E2E8F0] px-5 py-2.5 rounded-full shadow-sm">
              <div className="w-5 h-5 rounded-full bg-[#3C59B2] flex items-center justify-center flex-shrink-0">
                <span className="text-white text-xs font-bold">i</span>
              </div>
              <span className="text-sm text-gray-600">{US_HOME_PRICING_SECTION.creditsInfo}</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 max-w-6xl mx-auto">
          {plans.map((plan, index) => (
            <motion.div
              key={plan.videos}
              className={`card-hover bg-white rounded-3xl p-5 sm:p-6 md:p-8 border transition-all duration-300 relative flex flex-col ${
                plan.popular ? 'border-[#3C59B2] shadow-xl z-10 md:-translate-y-4' : 'border-[#E2E8F0]'
              }`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              {plan.savingsLabel ? (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-green-100 text-green-700 px-4 sm:px-6 py-1.5 rounded-full text-[10px] sm:text-xs font-bold shadow-md whitespace-nowrap">
                  {plan.savingsLabel}
                </div>
              ) : null}

              <div className="text-center mb-6">
                <h3 className="font-manrope text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-3">
                  {plan.videos} videos/month
                </h3>

                <div className={`text-3xl sm:text-4xl font-bold ${plan.popular ? 'text-[#3C59B2]' : 'text-slate-900'}`}>
                  ${plan.unitPrice}
                </div>
                <div className="text-sm text-slate-500 mt-1">{US_HOME_PRICING_SECTION.perVideo}</div>

                <div className="text-sm text-slate-500 mt-2">
                  <span className="font-semibold text-slate-600">${plan.monthlyPrice}/mo</span>
                </div>
              </div>

              <div className="flex-1 space-y-2 mb-6 sm:mb-8 border-t border-gray-100 pt-4 sm:pt-6">
                <FeatureRow text={`${plan.videos} credits/month`} />
                <FeatureRow text={US_HOME_PRICING_SECTION.walkthroughFeature} />
                <FeatureRow text="Unbranded version included (MLS-ready)" />
                <FeatureRow text="24-hour delivery (Mon-Thu)" />
                {tab === 'drone' && <FeatureRow text="drone clips included" highlight={true} />}
                <FeatureRow text="Additional credits available" isExtra={true} />
                <FeatureRow text="Friday → delivered Monday" useInfoIcon />
              </div>

              <a
                href="/signup"
                className={`btn-premium overflow-hidden w-full py-3 sm:py-4 rounded-full font-bold text-center transition-all flex items-center justify-center gap-2 text-sm sm:text-base ${
                  plan.popular
                    ? 'bg-[#3C59B2] text-white hover:bg-[#2d4a8f]'
                    : 'bg-white text-[#3C59B2] border-2 border-[#3C59B2] hover:bg-slate-50'
                }`}
              >
                Try One Property <ArrowRight className="w-4 h-4" />
              </a>
            </motion.div>
          ))}
        </div>

        <div className="mt-10 sm:mt-12 text-center max-w-3xl mx-auto">
          <p className="text-lg sm:text-xl font-semibold text-gray-900">Need more than 20 videos/month?</p>
          <p className="mt-2 text-gray-600 text-base sm:text-lg">
            We work with property managers, home builders, and real estate groups at any scale.
          </p>
          <div className="mt-5 flex justify-center">
            <a
              href="/custom-quote"
              className="btn-premium text-white px-8 py-3 rounded-full overflow-hidden font-bold transition-all bg-[#3C59B2] hover:bg-[#2d4a8f] inline-flex items-center justify-center gap-2"
            >
              Get a Custom Quote <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>

        <div className="mt-8 sm:mt-10 max-w-3xl mx-auto">
          <div className="bg-white border-l-[4px] border-[#3C59B2] p-6 rounded-xl shadow-[0_8px_24px_rgba(15,23,42,0.08)]">
            <div className="text-center">
            <h3 className="font-manrope whitespace-normal sm:whitespace-nowrap text-[clamp(1.125rem,4.2vw,3rem)] font-bold text-gray-900">
              Are you a Matterport photographer?
            </h3>
            <p className="mt-2 text-sm sm:text-base text-gray-600">
              Resell videos to your clients. Partner rates available.
            </p>
            <div className="mt-5 flex justify-center">
              <a
                href="/partners"
                className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-[#3C59B2] px-5 py-3 text-sm sm:text-base font-semibold text-[#3C59B2] transition-colors hover:bg-slate-50"
              >
                See partner pricing <ArrowRight className="w-4 h-4" />
              </a>
            </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HomePricing;

